import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign, TCampaignPrize } from '@/typings/api/publics/campaigns.type'
import {
  TCampaignAnalytic,
  TFollowerAnalytic,
  TTwitterCampaignAnalyticsParams
} from '@/typings/api/publics/twitter_campaign_analytics.type'

type AnalyticsPublicState = {
  campaignAnalytics: TCampaignAnalytic[]
  followerAnalytics: TFollowerAnalytic[]
  campaign: TCampaign | null
  prizes: TCampaignPrize[]
  params: TTwitterCampaignAnalyticsParams
  error: boolean
  loading: boolean
}

const createInitialState = (): AnalyticsPublicState => ({
  campaignAnalytics: [],
  followerAnalytics: [],
  campaign: null,
  prizes: [],
  params: {
    hash: '',
    prizeId: '',
    startDatetime: '',
    endDatetime: ''
  },
  error: false,
  loading: false
})

export const useTwitterAnalyticsPublicStore = defineStore('twitterAnalyticsPublicStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const campaignAnalytics = ref<AnalyticsPublicState['campaignAnalytics']>(initialState.campaignAnalytics)
  const followerAnalytics = ref<AnalyticsPublicState['followerAnalytics']>(initialState.followerAnalytics)
  const campaign = ref<AnalyticsPublicState['campaign']>(initialState.campaign)
  const prizes = ref<AnalyticsPublicState['prizes']>(initialState.prizes)
  const params = ref<AnalyticsPublicState['params']>(initialState.params)
  const error = ref<AnalyticsPublicState['error']>(initialState.error)
  const loading = ref<AnalyticsPublicState['loading']>(initialState.loading)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    campaignAnalytics.value = nextState.campaignAnalytics
    followerAnalytics.value = nextState.followerAnalytics
    campaign.value = nextState.campaign
    prizes.value = nextState.prizes
    params.value = nextState.params
    error.value = nextState.error
    loading.value = nextState.loading
  }

  const fetchCampaignAnalytics = async (): Promise<void> => {
    error.value = false
    loading.value = true
    await systemStore.setLoading(true)
    const result = await api.get('/publics/twitter_campaign_analytics', { params: params.value })
    loading.value = false
    await systemStore.setLoading(false)
    if (!result.data) {
      error.value = true
      return
    }
    campaignAnalytics.value = result.data
  }

  const fetchFollowerAnalytics = async (): Promise<void> => {
    error.value = false
    loading.value = true
    await systemStore.setLoading(true)
    const followerParams = {
      hash: params.value.hash,
      startDatetime: params.value.startDatetime,
      endDatetime: params.value.endDatetime
    }
    const result = await api.get('/publics/twitter_campaign_analytics/followers', { params: followerParams })
    loading.value = false
    await systemStore.setLoading(false)
    if (!result.data) {
      error.value = true
      return
    }
    followerAnalytics.value = result.data
  }

  const fetchCampaign = async (): Promise<void> => {
    error.value = false
    const result = await api.get('/publics/campaigns/' + params.value.hash)
    if (!result.data) {
      error.value = true
      return
    }
    campaign.value = result.data
  }

  const fetchPrizes = async (): Promise<void> => {
    error.value = false
    const result = await api.get(`/publics/campaigns/${params.value.hash}/prizes`)
    if (!result.data) {
      error.value = true
      return
    }
    prizes.value = result.data
  }

  const setParams = (newParams: Partial<TTwitterCampaignAnalyticsParams>): void => {
    params.value = { ...params.value, ...newParams }
  }

  return {
    campaignAnalytics: readonly(campaignAnalytics),
    followerAnalytics: readonly(followerAnalytics),
    campaign: readonly(campaign),
    prizes: readonly(prizes),
    params: readonly(params),
    error: readonly(error),
    loading: readonly(loading),
    reset,
    fetchCampaignAnalytics,
    fetchFollowerAnalytics,
    fetchCampaign,
    fetchPrizes,
    setParams
  }
})
